import React, {useState, useRef, useEffect, useContext, useMemo} from 'react';
import {Document, Page} from 'react-pdf';
import {useReactToPrint} from 'react-to-print';
import {TransformWrapper, TransformComponent} from "react-zoom-pan-pinch";
import {Container, Row, Col} from "react-bootstrap";
import ReportSearchParamBadge from "./ReportSearchParamBadge";
import AgGridToolbar from "components/AgGridToolbar/AgGridToolbar";
import useWindowDimensions from "hooks/Dimensions";
import PageHeader from "components/PageHeader";
import {saveAs} from 'file-saver';
import {ReportsPanelContext} from "./context";
import {ActionPanelContext} from "context/action";
import {SecurityContext} from "context/security";
import {REPORT_SAVED_REPORTS_URL} from "service/UrlConstant";
import {ToastContext} from "context/Toast";
import {dateToString} from "utils/FormatUtils";
import {EntityAccountsContext} from "components/entity-accounts/context";
import {REPORTS_PANEL_VIEW_REPORT_PAGE} from "./context/ReportsPanelContext";
import * as XLSX from 'xlsx';
import SpreadsheetViewer from "../../components/SpreadsheetViewer";


export default function ReportPdfPanel() {

    const {
        reportsDisplayState,
        setReportsDisplayState,
        reportsPanelPageState,
        clearReportSearchAndLoadReportsHome,
        showGridToolbar,
    } = useContext(ReportsPanelContext);

    const {
        makePutRequest,
        makeDeleteRequest,
        makePostRequest
    } = useContext(SecurityContext);

    const {
        setHandleSaveFunction,
        setHandleRenameFunction,
        setHandleDeleteFunction,
        setSavedSearchName
    } = useContext(ActionPanelContext);

    const {
        clearEntityAccounts
    } = useContext(EntityAccountsContext);

    const {success, error} = useContext(ToastContext);

    const {isMobile} = useWindowDimensions();

    const componentRef = useRef();
    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
    });

    const handlePrintFunction = ()=>{
        setIsPrintMode(true);
        setTimeout(()=>{
            handlePrint();
            setIsPrintMode(false);
        },500);
    }

    const zoomInRef = useRef(null);
    const zoomOutRef = useRef(null);
    const pageRefs = useRef([]);
    const inputRef = useRef(null);
    const [numPages, setNumPages] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [localPageNumber, setLocalPageNumber] = useState(1);
    const [isPrintMode, setIsPrintMode] = useState(false);
    const [deskTopScale, setDesktopScale] = useState(1.0);
    const [excelData, setExcelData] = useState([]);


    const saveReportSearch = (savedName, gridApi, onSuccess, onError) => {

        const onSuccessResponse = (response) => {
            if (onSuccess) onSuccess();
            reportsDisplayState.isReportSavedSearch = true;
            reportsDisplayState.reportSavedSearchName = response.data.savedName;
            reportsDisplayState.reportSavedSearchId = response.data.savedReportId;
            setReportsDisplayState(reportsDisplayState);
            setSavedSearchName(savedName);
            success("Successfully created saved report search");
        }

        const onErrorResponse = (err) => {
            console.error(err);
            if(onError) onError(err);
        }

        makePostRequest(
            {
                savedName: savedName,
                clientCode: reportsDisplayState.reportSearchData.clientCode,
                managerId: reportsDisplayState.reportSearchData.managerId,
                subManagerId: reportsDisplayState.reportSearchData.subManagerId,
                accountId: reportsDisplayState.reportSearchData.accountId,
                effectiveDate: dateToString(reportsDisplayState.reportSearchData.effectiveDate.toString()),
                folder: reportsDisplayState.reportSearchData.folder,
                fileName: reportsDisplayState.reportSearchData.fileName
            },
            REPORT_SAVED_REPORTS_URL, onSuccessResponse, onErrorResponse);
    };

    const updateReportSavedSearch = (updatedSavedName, gridApi, onSuccess, onError) => {

        const param = {
            savedName: updatedSavedName,
            savedReportId: reportsDisplayState.reportSavedSearchId,
            clientCode: reportsDisplayState.reportSearchData.clientCode,
            managerId: reportsDisplayState.reportSearchData.managerId,
            subManagerId: reportsDisplayState.reportSearchData.subManagerId,
            accountId: reportsDisplayState.reportSearchData.accountId,
            effectiveDate: dateToString(reportsDisplayState.reportSearchData.effectiveDate.toString()),
            folder: reportsDisplayState.reportSearchData.folder,
            fileName: reportsDisplayState.reportSearchData.fileName
        }

        const onSuccessResponse = (response) => {
            if (onSuccess) onSuccess();
            reportsDisplayState.reportSavedSearchName = updatedSavedName;
            setReportsDisplayState(reportsDisplayState);
            setSavedSearchName(updatedSavedName);
            success("Successfully updated saved report search");
        }

        const onErrorResponse = (err) => {
            console.error(err);
            if (onError) onError(err);
        }

        makePutRequest(param,
            REPORT_SAVED_REPORTS_URL + "/" + reportsDisplayState.reportSavedSearchId, onSuccessResponse, onErrorResponse);
    };

    const deleteReportSavedSearch = (gridApi, onSuccess, onError) => {

        const onSuccessResponse = (response) => {
            if (onSuccess) onSuccess();
            clearEntityAccounts();
            clearReportSearchAndLoadReportsHome();
            success("Report search successfully deleted");
        }

        const onErrorResponse = (err) => {
            console.error(err);
            if (onError) onError(err);
            error("Error occurred with deleting saved report search");
        }

        makeDeleteRequest(
            REPORT_SAVED_REPORTS_URL + "/" + reportsDisplayState.reportSavedSearchId, onSuccessResponse, onErrorResponse);
    };

    useEffect(() => {
        setLocalPageNumber(1)
        if (REPORTS_PANEL_VIEW_REPORT_PAGE === reportsPanelPageState.currentPage) {
            setHandleRenameFunction({handleRename: updateReportSavedSearch, gridApi: null});
            setHandleSaveFunction({handleSave: saveReportSearch, gridApi: null});
            setHandleDeleteFunction({handleDelete: deleteReportSavedSearch, gridApi: null});
        }
    },[reportsPanelPageState,reportsDisplayState])
useEffect(()=>{
    if(inputRef.current){
        inputRef.current.focus();
    }
}, [inputRef, localPageNumber])


    useEffect(() => {
        if (['csv', 'xlsx'].includes(reportsDisplayState.reportSearchData.fileType) && reportsDisplayState.reportData?.data) {
            try {

                let jsonData;
                if (reportsDisplayState.reportSearchData.fileType === 'csv') {
                    // Parse CSV data
                    const csvString = new TextDecoder().decode(reportsDisplayState.reportData.data);
                    jsonData = csvString.split('\n').map(row => row.split(','));
                } else {
                    // Parse XLSX data
                    const workbook = XLSX.read(reportsDisplayState.reportData.data, { type: 'array' });
                    const sheetName = workbook.SheetNames[0];
                    const sheet = workbook.Sheets[sheetName];
                    jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });
                }

                // Convert to x-data-spreadsheet format
                const formattedData = jsonData.reduce((acc, row, rowIndex) => {
                    if (row && row.length > 0) { // Ensure row is not empty
                        acc[rowIndex] = {
                            cells: row.reduce((cellAcc, cell, colIndex) => {
                                if (cell !== null && cell !== undefined) { // Ensure cell is not null/undefined
                                    cellAcc[colIndex] = { text: String(cell) }; // Convert cell to string
                                }
                                return cellAcc;
                            }, {}),
                        };
                    }
                    return acc;
                }, {});

                console.log("Formatted Data for Spreadsheet:", formattedData); // Debugging

                setExcelData(formattedData);
            } catch (error) {
                console.error('Error parsing Excel file:', error);
            }
        }
    }, [reportsDisplayState]);

    function onDocumentLoadSuccess({numPages}) {
        setNumPages(numPages);
        pageRefs.current = Array(numPages).fill().map(()=>React.createRef());
    }

    function pageTitle() {
        return ("Report");
    }

    function showPdfDocument() {
        const pdfNumPagesArray = [...Array(numPages).keys()];
        return (<>
            <Document ref={componentRef} file={reportsDisplayState.reportData} pageMode={"fullScreen"} pageLayout={"twoPageLeft"}
                      onLoadSuccess={onDocumentLoadSuccess} onLoadError={console.error} style={{scrollable:"auto"}}>
                {isPrintMode? pdfNumPagesArray.map(( index)=>(
                        <div className='printLayoutPage'>
                            <Page key={index + 1} pageNumber={index + 1} scale={isMobile ? 0.5 : deskTopScale} renderMode={"svg"} renderTextLayer={false}/>
                        </div>
                    )):<div style={{margin:0, height:"700px", overflowY:"auto", overflowX:"auto"}}>
                    {Array.apply(null, Array(numPages))
                        .map((x, i)=>i+1)
                        .map(page => {
                            return <div
                                className={'m-0 p-0'}
                                key={page}
                                ref={pageRefs.current[page-1]}>
                            <Page key={page}  pageNumber={page} scale={deskTopScale} renderMode={"svg"} renderTextLayer={false}/>
                            </div>
                        })}
            </div> }
            </Document>
        </>)
    }

    const RenderPdfDocument = useMemo(() =>
            showPdfDocument,
        [reportsDisplayState.reportData, deskTopScale, isPrintMode, numPages]);
    const goToPage = (pageNumber)=>{
        if(pageNumber >=1 && pageNumber <= numPages && !!pageRefs.current[pageNumber-1].current){
            pageRefs.current[pageNumber-1].current.scrollIntoView({
                behavior:"smooth",
                block:"start",
                inline:"nearest"
            })
            setPageNumber(pageNumber)
        }
    }
    const goToPageComponent = ()=>{
        return <><div style={{textAlign:"left"}}>
            <span>Go to page:</span>
            <input type="number"
                   className={"ml-2 mr-1"}
                   value={localPageNumber}
                   onChange={(e)=>setLocalPageNumber(e.target.value)}
                   name={'goToPage'}
                   ref = {inputRef}
                   onKeyPress={(e)=>{
                       if(e.key ==="Enter"){
                           goToPage(Number(e.target.value));
                       }
                   }}
                   min={1}
                   max={numPages}
                   style={{height:"25px", width:"50px"}}
            />
            of {numPages}
        </div>
        </>
    }


    function downloadReportToFile() {
        let blob = new Blob([reportsDisplayState.reportData.data], {type: "application/octet-stream"});
        saveAs(blob, reportsDisplayState.reportSearchData.fileName);
    }

    const DownloadPDF = () => {
        return (<>
            <a onClick={downloadReportToFile} className="linkColor"><span
                className="td-icon icon-small td-icon-download"></span></a>
        </>)
    }

    function PrintPDF() {
        return (<>
            <a onClick={() => {
                handlePrintFunction()
            }}>
                <span className="td-icon icon-small td-icon-printer"></span>
            </a>
        </>)
    }

    function ZoomInPDF() {
        return (<>
            <a onClick={() => {
                if(isMobile){
                    zoomInRef.current.click()
                }else{
                    setDesktopScale(scale => scale * 1.1);
                }
            }}>
                <i className="bi bi-zoom-in"></i>
            </a>
        </>)
    }

    function ZoomOutPDF() {
        return (<>
            <a onClick={() => {
                if(isMobile){
                    zoomOutRef.current.click()
                }else{
                    setDesktopScale(scale => scale / 1.1)
                }
            }}>
                <i className="bi bi-zoom-out"></i>
            </a>
        </>)
    }

    function Pagination() {
        return (<>
            <Container style={{width: "100%", color: "white", padding: 0}} fluid>
                <Row style={{width: "100%", marginLeft: 0, marginRight: 0}}>

                    <Col xs={6}>
                        <Container style={{color: "white"}} fluid>
                            <Row style={{color: "white", padding:0}}>
                                <Col>
                                    {goToPageComponent()}
                                </Col>
                            </Row>

                        </Container>
                    </Col>
                    <Col xs={6}>
                        <Container style={{color: "white"}} fluid>
                            <Row className={"flex-row-reverse"}>

                                <Col xs={1}>
                                    <PrintPDF/>
                                </Col>
                                <Col xs={1}>
                                    <DownloadPDF/>
                                </Col>

                                <Col xs={1}>
                                    <ZoomOutPDF/>
                                </Col>
                                <Col xs={1}>
                                    <ZoomInPDF/>
                                </Col>
                            </Row>

                        </Container>
                    </Col>
                </Row>
            </Container>
        </>)
    }
    return (<>
        <Container fluid>
            <PageHeader text={pageTitle()}>
                <ReportSearchParamBadge reportsDisplayState={reportsDisplayState}/>
            </PageHeader>

            {showGridToolbar && <AgGridToolbar reportScreen={true}
                                               title={reportsDisplayState.reportSavedSearchName}
                                               isSaved={reportsDisplayState.isReportSavedSearch}
                                               fileType ={reportsDisplayState.reportSearchData.fileType}
                                               handleDownload={downloadReportToFile}
            />}


                { !['xlsx', 'csv'].includes(reportsDisplayState.reportSearchData.fileType) && ( <Container style={{

                    }} fluid>
                <Row style={{paddingBottom: 0}}>
                    <Col style={{
                        textAlign: "center",
                        alignSelf: "center",
                        background: "#32353a",
                        width: "100%",
                        margin: 0,
                        padding: 0,
                        color: "#fdfdfe"
                    }} sm={12}>
                        <Pagination/>
                    </Col>
                </Row>
                <Row style={{paddingTop: 0}}>
                    <Col id="test" style={{
                        padding: 0,
                        alignContent: "center",
                        textAlign: "center",
                        alignSelf: "center"
                    }}>
                        {isMobile? <TransformWrapper defaultScale={1} defaultPositionX={100}
                                                     defaultPositionY={200} >
                            {({zoomIn, zoomOut, ...rest}) => (
                                <>
                                <span style={{display: "none"}}>
                                <a onClick={() => {
                                    zoomIn()
                                }} ref={zoomInRef}><i className="bi bi-zoom-in"></i></a>
                                <a onClick={() => {
                                    zoomOut()
                                }} ref={zoomOutRef}><i className="bi bi-zoom-out"></i></a>
                                </span>

                                    <TransformComponent>
                                        <RenderPdfDocument/>
                                    </TransformComponent>
                                </>
                            )}
                        </TransformWrapper> : <div>
                            <RenderPdfDocument/>
                        </div>}
                    </Col>
                </Row>
                    </Container>
)}

                    {['xlsx','csv'].includes(reportsDisplayState.reportSearchData.fileType) &&
                        <Container fluid className={'m-0 p-0'}>
                            <Row className={'m-0 p-0'}>
                                <Col className={'m-0 p-0'}>
                                    <SpreadsheetViewer excelData={excelData} fileType={reportsDisplayState.reportSearchData.fileType} />
                                </Col>
                            </Row>
                        </Container>
                    }

        </Container>
    </>);
}